import React, {useEffect, useState} from 'react';
import {CircularProgress} from '@mui/material';
import FieldHelpSuffix from '../common/FieldHelpSuffix';
import { useTranslation } from 'react-i18next';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {
    ErrorText,
    InputField,
    StyledDialog,
    StyledDialogActions,
    StyledDialogContent,
    StyledDialogTitle
} from '../data-sources/CreateDataSourceDialog.styled';

type ValidateFn = (value: string) => string | null;

interface GenericInputDialogProps {
    open: boolean;
    title: string;
    label: string;
    initialValue?: string;
    helperText?: string;
    validate?: ValidateFn;
    loading?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;

    placeholder?: string;
    required?: boolean;
    multiline?: boolean;
    rows?: number;
    type?: React.HTMLInputTypeAttribute;

    onClose: () => void;
    onConfirm: (value: string) => void;
}

const GenericInputDialog: React.FC<GenericInputDialogProps> = ({
                                                                   open,
                                                                   title,
                                                                   label,
                                                                   initialValue = '',
                                                                   helperText,
                                                                   validate,
                                                                   loading = false,
                                                                   placeholder,
                                                                   confirmButtonText = 'common.save',
                                                                   cancelButtonText = 'common.cancel',
                                                                   required = false,
                                                                   multiline = false,
                                                                   rows = 3,
                                                                   type = 'text',
                                                                   onClose,
                                                                   onConfirm
                                                               }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (open) {
            setValue(initialValue);
            setError('');
        }
    }, [open, initialValue]);

    const isEmptyInvalid = required && value.trim().length === 0;

    const runValidation = (val: string): string => {
        if (isEmptyInvalid) return 'Pole jest wymagane.';
        const msg = validate ? validate(val) : null;
        return msg ?? '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = e.target.value;
        setValue(next);
        if (error) setError('');
    };

    const handleSubmit = () => {
        const msg = runValidation(value);
        if (msg) {
            setError(msg);
            return;
        }
        onConfirm(value.trim());
    };

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        if (e.key === 'Enter' && !multiline && !loading) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const disabledConfirm = loading || isEmptyInvalid || !!error;

    return (
        <StyledDialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="generic-input-dialog-title">
            <StyledDialogTitle id="generic-input-dialog-title">{title}</StyledDialogTitle>

            <StyledDialogContent>
                <InputField
                    label={label}
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    error={!!error}
                    placeholder={placeholder}
                    required={required}
                    multiline={multiline}
                    rows={multiline ? rows : undefined}
                    type={type}
                    inputProps={{
                        'aria-label': String(label),
                    }}
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix
                                asAdornment
                                title={t('inputDialog.help.value')}
                                ariaLabel={t('inputDialog.help.value') as string}
                            />
                        )
                    }}
                />
                {helperText && !error && <ErrorText as="div" style={{opacity: 0.7}}>{helperText}</ErrorText>}
                {error && <ErrorText>{error}</ErrorText>}
            </StyledDialogContent>

            <StyledDialogActions>
                <CancelButton onClick={onClose} disabled={loading}>
                    {t(cancelButtonText)}
                </CancelButton>
                <ActionButton onClick={handleSubmit} disabled={disabledConfirm}>
                    {loading ? <CircularProgress size={24} color="inherit" aria-hidden={true}/> : t(confirmButtonText)}
                </ActionButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default GenericInputDialog;
